
import React from 'react';
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, ThumbsUp, MessageSquare, Bookmark, Share2, Users } from "lucide-react";

interface ProductHuntPreviewProps {
  productName?: string;
  tagline?: string;
  shortDescription?: string;
  fullDescription?: string;
  userName?: string;
}

export const ProductHuntPreview: React.FC<ProductHuntPreviewProps> = ({
  productName = "Your Product",
  tagline = "Your catchy tagline goes here",
  shortDescription = "Brief description of what your product does and who it's for",
  fullDescription = "",
  userName = "You"
}) => {
  // Function to format description text with bold for ** ** patterns
  const formatDescription = (text: string) => {
    if (!text) return '';
    
    // Split by ** markers
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, index) => {
      // Check if this part is wrapped in ** **
      if (part.startsWith('**') && part.endsWith('**')) {
        // Remove the ** markers and make it bold
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      // Return normal text
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  return (
    <div className="flex flex-col space-y-6 w-full">
      {/* Header Preview - Redesigned to match Product Hunt layout */}
      <div className="rounded-lg overflow-hidden">
        {/* Product image banner - taller height */}
        <div className="relative h-72 bg-black">
          {/* Product grid pattern overlay */}
          <div className="absolute inset-0 grid grid-cols-6 grid-rows-2 gap-2 p-4 opacity-70">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="bg-gray-800/20 rounded-md flex items-center justify-center">
                {i % 3 === 0 && <div className="w-8 h-8 rounded-full bg-gray-300/20"></div>}
              </div>
            ))}
          </div>
          
          {/* Product info at bottom left */}
          <div className="absolute bottom-0 left-0 p-4 flex items-end gap-4">
            {/* Product icon */}
            <div className="h-16 w-16 rounded-lg bg-gradient-to-r from-orange-500 to-orange-400 flex items-center justify-center text-white text-2xl font-bold">
              {productName.charAt(0)}
            </div>
            
            {/* Product name and description */}
            <div className="text-white">
              <div className="flex items-center">
                <h1 className="text-xl font-bold mr-2 flex items-center">
                  {productName}
                  <span className="inline-flex ml-2 text-sm font-normal opacity-80">↓</span>
                </h1>
              </div>
              <p className="text-sm text-gray-200">{tagline}</p>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="absolute bottom-4 right-4 flex gap-2">
            <Button 
              size="sm"
              variant="secondary" 
              className="bg-white text-gray-800 hover:bg-gray-100 border-none"
            >
              <ExternalLink className="h-4 w-4 mr-1" /> Visit
            </Button>
            <Button 
              className="bg-red-500 hover:bg-red-600 text-white" 
              size="sm"
            >
              <ThumbsUp className="h-4 w-4 mr-1" /> Upvote 160
            </Button>
          </div>
        </div>
      </div>

      {/* Short Description Preview */}
      <div className="px-1 py-2">
        <p className="text-base text-zinc-700 dark:text-zinc-300">
          {shortDescription}
        </p>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-zinc-600 dark:text-zinc-400">Launch tags:</div>
        <Badge variant="outline" className="rounded-full bg-zinc-100 text-zinc-700 border-zinc-200 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700">
          SaaS
        </Badge>
        <Badge variant="outline" className="rounded-full bg-zinc-100 text-zinc-700 border-zinc-200 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700">
          AI
        </Badge>
        <Badge variant="outline" className="rounded-full bg-zinc-100 text-zinc-700 border-zinc-200 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700">
          Marketing
        </Badge>
      </div>

      {/* Payment label */}
      <div className="text-sm text-zinc-600 dark:text-zinc-400">
        Payment Required
      </div>

      {/* Full Description Preview */}
      {fullDescription && (
        <div className="border rounded-lg p-6 mt-6">
          <div className="flex items-center space-x-3 mb-6">
            <Avatar className="h-10 w-10 border">
              <div className="bg-blue-100 dark:bg-blue-900 w-full h-full flex items-center justify-center text-blue-600 dark:text-blue-300 font-semibold">
                {userName?.charAt(0) || "U"}
              </div>
            </Avatar>
            <div>
              <div className="font-medium">{userName}</div>
              <div className="text-sm text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
                <Badge variant="outline" className="h-5 px-1 py-0 text-xs bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
                  Maker
                </Badge>
              </div>
            </div>
          </div>
          
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="font-medium mb-4">Hello Product Hunters! 👋</p>
            
            <div className="whitespace-pre-wrap">
              {formatDescription(fullDescription)}
            </div>
          </div>
          
          <div className="flex items-center gap-3 mt-6 pt-6 border-t">
            <Button variant="outline" size="sm" className="gap-1">
              <ThumbsUp className="h-4 w-4" /> Upvote (0)
            </Button>
            <Button variant="outline" size="sm" className="gap-1">
              <MessageSquare className="h-4 w-4" /> Reply
            </Button>
            <Button variant="outline" size="sm" className="p-0 w-9">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Team section preview */}
      <div className="rounded-lg border p-4 mt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <Users className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </div>
            <span className="font-medium">Meet the team</span>
          </div>
          <Button variant="outline" size="sm" className="gap-1">
            Show more <span className="text-xs">↓</span>
          </Button>
        </div>
        <div className="flex mt-4 gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Avatar key={i} className="h-10 w-10 border">
              <div className="bg-gray-100 dark:bg-gray-800 w-full h-full"></div>
            </Avatar>
          ))}
        </div>
      </div>
    </div>
  );
};
